export const manualTask = 'eb20cbe5-62ad-4046-b0d2-53fc2e2b89d1'
export const manualRepo = 'the-Otomator/otomator-admin'
export type ManualPr = { repo: string; number: number; head_sha: string; state: string; draft: boolean; task?: { id: string } | null }
export type ManualManifest = {
  schema: string; task_id: string
  pr: { repo: string; number: number; head_sha: string; head_branch: string; base_branch: string; task_id: string }
  sql_items: { database: string; repo: string; pr_number: number; head_sha: string; version: string; name: string; path: string; sha256: string; sql: string }[]
}
export type ManualPrepared = { manifest: ManualManifest; fingerprint: string; sql_digest: string; blockers: string[] }
export type ManualState = {
  approval?: { id: string; approval_id: string; owner_id: string; manifest: ManualManifest; fingerprint: string; sql_digest: string; scope: string }
  state?: { valid: boolean; ready: boolean; checked_at?: string; reason: string }
  items?: { id: string; version: string; name: string; status: string; emergency: boolean; held: boolean; approved_by: string | null; manual_sql_approval_id: string; git_sha: string; sql_sha256: string }[]
}
export function manualEligible(pr: ManualPr) {
  return pr.repo === manualRepo && [245,246,248,249].includes(pr.number) && pr.task?.id === manualTask && pr.state === 'open' && !pr.draft && /^[a-f0-9]{40}$/.test(pr.head_sha)
}
export function manualIdentity(pr: ManualPr) { return JSON.stringify([pr.repo,pr.number,pr.head_sha,pr.task?.id,pr.state,pr.draft]) }
export function canonical(value: unknown): string {
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']'
  if (value && typeof value === 'object') return '{' + Object.keys(value).sort().map(k => JSON.stringify(k)+':'+canonical((value as Record<string,unknown>)[k])).join(',') + '}'
  return JSON.stringify(value)
}
export async function digest(value: string) {
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(value))), b => b.toString(16).padStart(2,'0')).join('')
}
export async function validateManualPreparation(value: ManualPrepared, pr: ManualPr) {
  try {
    const m = value.manifest, p = m.pr
    if (!manualEligible(pr) || m.schema !== 'otomator-manual-sql-v1' || m.task_id !== manualTask || p.task_id !== manualTask || p.repo !== pr.repo || p.number !== pr.number || p.head_sha !== pr.head_sha || p.base_branch !== 'master' || !p.head_branch || ['main','master'].includes(p.head_branch)) return false
    if (!Array.isArray(value.blockers) || !value.blockers.every(b=>typeof b==='string') || !Array.isArray(m.sql_items) || !m.sql_items.length || m.sql_items.length>20) return false
    const versions = new Set<string>()
    for (const s of m.sql_items) {
      if (s.database !== 'admin' || s.repo !== pr.repo || s.pr_number !== pr.number || s.head_sha !== pr.head_sha || !/^\d{14}$/.test(s.version) || !/^[a-zA-Z0-9_]+$/.test(s.name) || s.path !== `supabase/migrations/${s.version}_${s.name}.sql` || versions.has(s.version) || typeof s.sql !== 'string' || !s.sql.trim() || await digest(s.sql) !== s.sha256) return false
      versions.add(s.version)
    }
    return await digest(canonical(m)) === value.fingerprint && await digest([...m.sql_items].sort((a,b)=>a.version.localeCompare(b.version)).map(s=>`${s.version}:${s.sha256}`).join('\n')) === value.sql_digest
  } catch { return false }
}
export async function validateManualState(value: ManualState, pr: ManualPr) {
  const a=value.approval
  if (!a || a.scope !== 'manual_sql_owner_merge' || !value.state || !Array.isArray(value.items) || !await validateManualPreparation({...a,blockers:[]},pr) || a.id !== a.approval_id || value.items.length !== a.manifest.sql_items.length) return false
  const ids=new Set<string>(), versions=new Set<string>()
  return value.items.every(q=>{
    const s=a.manifest.sql_items.find(s=>s.version===q.version)
    if (!s || ids.has(q.id) || versions.has(q.version)) return false
    ids.add(q.id); versions.add(q.version)
    return q.manual_sql_approval_id===a.id && q.git_sha===pr.head_sha && q.sql_sha256===s.sha256 && q.name===s.name && q.emergency===true
  })
}
export function manualApplied(value: ManualState) {
  return !!value.state?.valid && !!value.items?.length && value.items.every(q=>q.status==='applied' && !q.held && q.approved_by===value.approval?.owner_id)
}
export type Journal = { request_id: string; fingerprint: string; head: string }
export function journalKey(pr: ManualPr) { return `manual-sql-request:${pr.repo}:${pr.number}` }
export function readJournal(storage: Pick<Storage,'getItem'>,pr: ManualPr): Journal | null {
  const raw=storage.getItem(journalKey(pr)); if (!raw) return null
  const j=JSON.parse(raw) as Journal
  if (!/^[a-f0-9-]{36}$/i.test(j.request_id) || !/^[a-f0-9]{64}$/.test(j.fingerprint) || !/^[a-f0-9]{40}$/.test(j.head)) throw Error('רישום הבקשה המקומי אינו תקין. יש לבדוק בחדר הבקרה.')
  return j
}
export function persistJournal(storage: Pick<Storage,'getItem'|'setItem'>,pr: ManualPr,p: ManualPrepared): Journal {
  const old=readJournal(storage,pr)
  if (old) throw Error('קיימת בקשה קודמת. יש לבדוק את מצבה לפני הכנה חדשה.')
  const j={request_id:crypto.randomUUID(),fingerprint:p.fingerprint,head:pr.head_sha}
  storage.setItem(journalKey(pr),JSON.stringify(j)); return j
}
export function existingJournal(storage: Pick<Storage,'getItem'>,pr: ManualPr,p: ManualPrepared,requestId: string): Journal {
  const j=readJournal(storage,pr)
  if(!j || j.request_id!==requestId || j.head!==pr.head_sha || j.fingerprint!==p.fingerprint) throw Error('הבקשה או הגרסה השתנו. יש לבדוק את המזהה המקורי; לא נוצר מזהה חדש.')
  return j
}
// Caller holds the same cross-tab Web Lock used by initial save/retry.
export function archiveJournal(storage: Pick<Storage,'getItem'|'setItem'|'removeItem'>,pr: ManualPr,expected: Journal) {
  const current=readJournal(storage,pr)
  if(!current || current.request_id!==expected.request_id || current.head!==expected.head || current.fingerprint!==expected.fingerprint) throw Error('בקשה אחרת החליפה את הרישום. יש לקרוא את מצבה; הרישום לא נמחק.')
  storage.setItem(`${journalKey(pr)}:history:${current.request_id}`,JSON.stringify(current))
  storage.removeItem(journalKey(pr))
}
